import { _ } from "../../core/polyglot/polyglot.js";
import {
	areConsecutiveDays,
	durationAsMonths,
	isSameDay,
	newDate,
} from "../../core/helpers/date-util.js";
import {
	formatAsDashedYearMonthDay,
	formatAsFullYearMonthAndDay,
	formatAsIsoDateAndTime,
	formatAsLongDate,
} from "../../core/helpers/date-formatting-util.js";
import Model from "../Model.js";

/**
 * Timespan is a simple `start` to `end` time interval.
 *
 * @param {{}} spec - Parameter object
 * @param {Date} spec.start - Beginning of the interval
 * @param {Date} spec.end - End of the interval
 */
export default class Timespan extends Model {
	constructor({ start, end } = {}) {
		super(...arguments);

		this._start = start;
		this._end = end;
	}

	getStart() {
		return this._start;
	}

	getEnd() {
		return this._end;
	}

	includes(candidate) {
		if (candidate instanceof Date) {
			return candidate >= this._start && candidate <= this._end;
		}

		return this.includesAllOf(candidate);
	}

	includesAllOf(candidate) {
		return (
			candidate.getStart() >= this._start &&
			candidate.getEnd() <= this._end
		);
	}

	includesAnyOf(candidate) {
		return (
			this.includes(candidate.getStart()) ||
			this.includes(candidate.getEnd()) ||
			candidate.includes(this._start) ||
			candidate.includes(this._end)
		);
	}

	endsBefore(candidate) {
		if (!candidate) {
			return false;
		}
		if (candidate instanceof Date) {
			return this._end < candidate;
		}

		return this.endsBefore(candidate.getStart());
	}

	endsAtOrBefore(candidate) {
		if (!(candidate instanceof Date)) {
			return this.endsAtOrBefore(candidate.getEnd());
		}

		return this.isEndingAt(candidate) || this.endsBefore(candidate);
	}

	isStartingAt(startDate) {
		return isSameDay(this._start, startDate);
	}

	isEndingAt(endDate) {
		return isSameDay(this._end, endDate);
	}

	equates(other) {
		return (
			this.isStartingAt(other.getStart()) &&
			this.isEndingAt(other.getEnd())
		);
	}

	getStartDateString() {
		return this._formatIso8601Date(this._start);
	}

	getEndDateString() {
		return this._formatIso8601Date(this._end);
	}

	toSmartString() {
		if (areConsecutiveDays(this._start, this._end)) {
			return this._formatLocalDate(this._start);
		}

		return _("{fromDate} to {toDate}", {
			fromDate: this._formatLocalDate(this._start),
			toDate: this._formatLocalDate(this._end),
		});
	}

	toString() {
		return `Custom-${formatAsFullYearMonthAndDay(
			this._start,
		)}-${formatAsFullYearMonthAndDay(this._end)}`;
	}

	intersect(anotherTimespan) {
		if (!this.includesAnyOf(anotherTimespan)) {
			throw new Error("Timespans must intersect");
		}

		return new Timespan({
			start:
				this._start < anotherTimespan.getStart()
					? anotherTimespan.getStart()
					: this._start,
			end:
				this._end < anotherTimespan.getEnd()
					? this._end
					: anotherTimespan.getEnd(),
		});
	}

	durationAsMonths() {
		return durationAsMonths({
			from: this.getStart(),
			to: this.getEnd(),
		});
	}

	_formatLocalDate(string) {
		return formatAsLongDate(string);
	}

	_formatIso8601Date(string) {
		return formatAsDashedYearMonthDay(string);
	}

	_serializedProperties() {
		const data = super._serializedProperties();

		data.start = formatAsIsoDateAndTime(this._start);
		data.end = formatAsIsoDateAndTime(this._end);

		return data;
	}

	static transformMaterializationData(data) {
		data.start = newDate(data.start);
		data.end = newDate(data.end);
	}
}
